/**
 * Pending Reviews — multi-step workflow inbox (Varo, May 2026).
 *
 * Shows every approval currently in flight (PENDING_REVIEW, UNDER_REVIEW,
 * SECOND_REVIEW, LEGAL_REVIEW, RETURNED) for the teams the user reviews.
 *
 * Actions per row depend on the current status:
 *   PENDING_REVIEW   → Pickup, Approve, Return, Reject
 *   UNDER_REVIEW     → Approve, Route, Return, Reject
 *   SECOND_REVIEW    → Approve, Route, Return, Reject
 *   LEGAL_REVIEW     → Approve, Route, Return, Reject
 *   RETURNED         → (analyst's responsibility — listed for visibility only)
 *
 * Columns: file, status badge, stale badge, current reviewer, submitter,
 * team, time-in-stage, actions.
 *
 * Route modal lets the reviewer pick a target stage (UNDER / SECOND / LEGAL)
 * and a reviewer from a dropdown of team members. Per Varo product call,
 * no auto-assign — explicit reviewerId is required by the backend.
 *
 * Return modal collects a free-text reason that's appended to reviewNotes
 * and shown to the submitter. Different from Reject — Return is in-flight
 * and expects a resubmit; Reject is terminal.
 */

import { useState, useMemo, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { approvalApi, rbacApi } from '../lib/api';
import {
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  UserIcon,
  UserGroupIcon,
  ArrowUturnLeftIcon,
  ArrowsRightLeftIcon,
  HandRaisedIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';

import ReviewSlideOver from '../components/ReviewSlideOver';
import ApprovalStatus, { type ApprovalStatusValue } from '../components/ApprovalStatus';

// ─── Types ──────────────────────────────────────────────────────────────────

type RouteTarget = 'UNDER_REVIEW' | 'SECOND_REVIEW' | 'LEGAL_REVIEW';

interface ReviewRow {
  id: string;
  status: ApprovalStatusValue;
  stageEnteredAt: string | null;
  staleAfterDays: number;
  submittedAt: string | null;
  report: {
    id: string;
    teamId: string | null;
    upload: { fileName: string; fileType: string };
    user: { email: string; firstName: string | null; lastName: string | null };
    team: { id: string; name: string } | null;
  };
  submitter: { firstName: string | null; lastName: string | null; email: string } | null;
  reviewer: { id: string; firstName: string | null; lastName: string | null; email: string } | null;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const ACTIVE_REVIEW_STATUSES: ApprovalStatusValue[] = [
  'PENDING_REVIEW', 'UNDER_REVIEW', 'SECOND_REVIEW', 'LEGAL_REVIEW',
];

function isStale(row: ReviewRow, now = new Date()): boolean {
  if (row.status === 'APPROVED' || row.status === 'REJECTED' || row.status === 'DRAFT') return false;
  if (!row.stageEnteredAt) return false;
  const ageMs = now.getTime() - new Date(row.stageEnteredAt).getTime();
  const thresholdMs = row.staleAfterDays * 24 * 60 * 60 * 1000;
  return ageMs > thresholdMs;
}

function userLabel(u: { firstName: string | null; lastName: string | null; email: string } | null): string {
  if (!u) return '—';
  const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
  return name || u.email;
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function PendingReviewsPage() {
  const queryClient = useQueryClient();

  // Action UI state
  const [rejectingReport, setRejectingReport] = useState<string | null>(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [returningReport, setReturningReport] = useState<string | null>(null);
  const [returnReason, setReturnReason] = useState('');
  const [routingReport, setRoutingReport] = useState<{ reportId: string; teamId: string | null } | null>(null);
  const [routingTarget, setRoutingTarget] = useState<RouteTarget>('SECOND_REVIEW');
  const [routingReviewerId, setRoutingReviewerId] = useState('');
  const [routingNote, setRoutingNote] = useState('');
  const [selectedReviewIndex, setSelectedReviewIndex] = useState<number | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['pending-reviews'],
    queryFn: async () => {
      const response = await approvalApi.getPendingReviews();
      return response.data;
    },
  });

  // Reviewer dropdown — only fetch when the Route modal opens, scoped to
  // the current row's team. Filtering by role would need a new RBAC API
  // (TODO); for v1 we surface every team member and let the actor pick.
  const { data: teamMembersData } = useQuery({
    queryKey: ['team-members', routingReport?.teamId],
    queryFn: () => rbacApi.getTeamMembers(routingReport!.teamId!).then(r => r.data),
    enabled: !!routingReport?.teamId,
  });
  const teamMembers: Array<{ id: string; name: string; email: string; role: string }> =
    teamMembersData?.members || teamMembersData || [];

  // Mutations ---------------------------------------------------------------
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['pending-reviews'] });

  const approveMutation = useMutation({
    mutationFn: (reportId: string) => approvalApi.approve(reportId),
    onSuccess: () => { toast.success('Approved'); setSelectedReviewIndex(null); invalidate(); },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Approve failed'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ reportId, notes }: { reportId: string; notes: string }) =>
      approvalApi.reject(reportId, notes),
    onSuccess: () => {
      toast.success('Rejected');
      setRejectingReport(null);
      setRejectNotes('');
      setSelectedReviewIndex(null);
      invalidate();
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Reject failed'),
  });

  const pickupMutation = useMutation({
    mutationFn: (reportId: string) => approvalApi.pickup(reportId),
    onSuccess: () => { toast.success('Picked up'); invalidate(); },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Pickup failed'),
  });

  const routeMutation = useMutation({
    mutationFn: () => approvalApi.route(routingReport!.reportId, {
      targetStatus: routingTarget,
      reviewerId: routingReviewerId,
      note: routingNote.trim() || undefined,
    }),
    onSuccess: () => {
      toast.success(`Routed to ${routingTarget.replace('_', ' ').toLowerCase()}`);
      setRoutingReport(null);
      setRoutingReviewerId('');
      setRoutingNote('');
      invalidate();
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Route failed'),
  });

  const returnMutation = useMutation({
    mutationFn: ({ reportId, reason }: { reportId: string; reason: string }) =>
      approvalApi.returnToBusiness(reportId, reason),
    onSuccess: () => {
      toast.success('Returned to submitter');
      setReturningReport(null);
      setReturnReason('');
      invalidate();
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Return failed'),
  });

  // ── Render ───────────────────────────────────────────────────────────────

  const reviews: ReviewRow[] = useMemo(() => data?.pendingReviews || [], [data?.pendingReviews]);
  const staleCount = useMemo(() => reviews.filter(r => isStale(r)).length, [reviews]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  const handleSubmitRoute = (e: FormEvent) => {
    e.preventDefault();
    if (!routingReviewerId) {
      toast.error('Pick a reviewer to route to');
      return;
    }
    routeMutation.mutate();
  };

  const handleSubmitReturn = (e: FormEvent) => {
    e.preventDefault();
    if (!returningReport || !returnReason.trim()) return;
    returnMutation.mutate({ reportId: returningReport, reason: returnReason });
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Approval Workflow</h1>
          <p className="mt-2 text-gray-600">
            {data?.count || 0} report{data?.count !== 1 ? 's' : ''} in your review queue
            {staleCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-amber-700">
                · <ExclamationTriangleIcon className="h-4 w-4" />
                {staleCount} stale
              </span>
            )}
          </p>
        </div>
        <button onClick={() => refetch()} className="text-sm text-ocean-600 hover:text-ocean-700 font-medium">
          Refresh
        </button>
      </div>

      {reviews.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900">All caught up</h3>
          <p className="text-gray-500 mt-1">No reports are waiting for your review.</p>
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ad File</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reviewer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitter</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">In stage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reviews.map((review, idx) => {
                  const reportId = review.report.id;
                  const stale = isStale(review);
                  const anyMutationPending =
                    approveMutation.isPending ||
                    rejectMutation.isPending ||
                    pickupMutation.isPending ||
                    routeMutation.isPending ||
                    returnMutation.isPending;
                  const isActive = ACTIVE_REVIEW_STATUSES.includes(review.status);
                  const isReturned = review.status === 'RETURNED';

                  return (
                    <tr key={review.id} className={`hover:bg-gray-50 ${stale ? 'bg-amber-50/30' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3" />
                          <div>
                            <Link to={`/reports/${reportId}`} className="text-sm font-medium text-ocean-600 hover:text-ocean-700">
                              {review.report.upload.fileName}
                            </Link>
                            <p className="text-xs text-gray-500">{review.report.upload.fileType}</p>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1 items-start">
                          <ApprovalStatus status={review.status} />
                          {stale && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded">
                              <ExclamationTriangleIcon className="h-3 w-3" />
                              STALE
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {review.reviewer ? userLabel(review.reviewer) : <span className="text-gray-400">unassigned</span>}
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-700">
                          <UserIcon className="h-4 w-4 text-gray-400 mr-1.5" />
                          {userLabel(review.submitter)}
                        </div>
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-700">
                          <UserGroupIcon className="h-4 w-4 text-gray-400 mr-1.5" />
                          {review.report.team?.name || '—'}
                        </div>
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {review.stageEnteredAt
                          ? formatDistanceToNow(new Date(review.stageEnteredAt), { addSuffix: true })
                          : '—'}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {/* PENDING_REVIEW: pickup */}
                          {review.status === 'PENDING_REVIEW' && (
                            <button
                              onClick={() => pickupMutation.mutate(reportId)}
                              disabled={anyMutationPending}
                              title="Take ownership of this review"
                              className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-white bg-teal-600 rounded hover:bg-teal-700 disabled:opacity-50"
                            >
                              <HandRaisedIcon className="h-3.5 w-3.5 mr-1" />
                              Pick up
                            </button>
                          )}

                          {/* Active review states: approve, route, return, reject */}
                          {isActive && (
                            <>
                              <button
                                onClick={() => approveMutation.mutate(reportId)}
                                disabled={anyMutationPending}
                                className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
                              >
                                <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                                Approve
                              </button>
                              {review.status !== 'PENDING_REVIEW' && (
                                <button
                                  onClick={() => {
                                    setRoutingReport({ reportId, teamId: review.report.teamId });
                                    setRoutingTarget(review.status === 'UNDER_REVIEW' ? 'SECOND_REVIEW' : 'LEGAL_REVIEW');
                                    setRoutingReviewerId('');
                                    setRoutingNote('');
                                  }}
                                  disabled={anyMutationPending}
                                  className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                                >
                                  <ArrowsRightLeftIcon className="h-3.5 w-3.5 mr-1" />
                                  Route
                                </button>
                              )}
                              <button
                                onClick={() => { setReturningReport(reportId); setReturnReason(''); }}
                                disabled={anyMutationPending}
                                className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-amber-800 bg-amber-100 border border-amber-200 rounded hover:bg-amber-200 disabled:opacity-50"
                              >
                                <ArrowUturnLeftIcon className="h-3.5 w-3.5 mr-1" />
                                Return
                              </button>
                              <button
                                onClick={() => { setRejectingReport(reportId); setRejectNotes(''); }}
                                disabled={anyMutationPending}
                                className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
                              >
                                <XCircleIcon className="h-3.5 w-3.5 mr-1" />
                                Reject
                              </button>
                            </>
                          )}

                          {/* RETURNED: visibility only — submitter owns it */}
                          {isReturned && (
                            <span className="text-xs text-amber-700 italic">Awaiting submitter resubmit</span>
                          )}

                          <button
                            onClick={() => setSelectedReviewIndex(idx)}
                            className="text-xs font-medium text-ocean-600 hover:text-ocean-700 ml-1"
                          >
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── Route modal ─── */}
      {routingReport && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={() => setRoutingReport(null)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Route to another reviewer</h2>
            <p className="text-sm text-gray-600 mb-4">
              Pick the next stage and the reviewer who'll pick up. The submitter stays informed via the activity log.
            </p>
            <form onSubmit={handleSubmitRoute} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Target stage</label>
                <select
                  value={routingTarget}
                  onChange={e => setRoutingTarget(e.target.value as RouteTarget)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-500"
                >
                  <option value="UNDER_REVIEW">Under Review (back to first reviewer)</option>
                  <option value="SECOND_REVIEW">Second Review (senior compliance)</option>
                  <option value="LEGAL_REVIEW">Legal Review</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Reviewer</label>
                <select
                  value={routingReviewerId}
                  onChange={e => setRoutingReviewerId(e.target.value)}
                  required
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-500"
                >
                  <option value="">— Select a reviewer —</option>
                  {teamMembers.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.name || m.email} · {m.role}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Note (optional)</label>
                <textarea
                  value={routingNote}
                  onChange={e => setRoutingNote(e.target.value)}
                  rows={3}
                  placeholder="Why are you routing this? Saved on the approval and visible to the next reviewer."
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-500"
                />
              </div>
              <div className="flex gap-2 pt-2 justify-end">
                <button type="button" onClick={() => setRoutingReport(null)} className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={routeMutation.isPending || !routingReviewerId}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-semibold"
                >
                  {routeMutation.isPending ? 'Routing…' : 'Route'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Return modal ─── */}
      {returningReport && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={() => setReturningReport(null)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Return for fixes</h2>
            <p className="text-sm text-gray-600 mb-4">
              The submitter sees this reason and can resubmit after addressing it. Use <strong>Reject</strong> instead
              if this submission cannot be approved at all.
            </p>
            <form onSubmit={handleSubmitReturn} className="space-y-3">
              <textarea
                value={returnReason}
                onChange={e => setReturnReason(e.target.value)}
                placeholder="What needs to be fixed?"
                rows={4}
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setReturningReport(null)} className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!returnReason.trim() || returnMutation.isPending}
                  className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-semibold"
                >
                  {returnMutation.isPending ? 'Returning…' : 'Send back to submitter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Reject modal (terminal) ─── */}
      {rejectingReport && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={() => setRejectingReport(null)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Reject (terminal)</h2>
            <p className="text-sm text-gray-600 mb-4">
              Use this only when the submission cannot be approved as-is. For minor fixes that should come back,
              use <strong>Return</strong> instead.
            </p>
            <textarea
              value={rejectNotes}
              onChange={e => setRejectNotes(e.target.value)}
              placeholder="Rejection notes — visible to the submitter."
              rows={4}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setRejectingReport(null); setRejectNotes(''); }} className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900">
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!rejectingReport || !rejectNotes.trim()) return;
                  rejectMutation.mutate({ reportId: rejectingReport, notes: rejectNotes });
                }}
                disabled={!rejectNotes.trim() || rejectMutation.isPending}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-semibold"
              >
                {rejectMutation.isPending ? 'Rejecting…' : 'Confirm rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Slide-over (existing component) */}
      <ReviewSlideOver
        isOpen={selectedReviewIndex !== null}
        onClose={() => setSelectedReviewIndex(null)}
        reportId={selectedReviewIndex !== null ? reviews[selectedReviewIndex]?.report?.id ?? null : null}
        reviews={reviews}
        currentIndex={selectedReviewIndex ?? 0}
        onNavigate={(idx) => setSelectedReviewIndex(idx)}
        onApprove={(reportId) => approveMutation.mutate(reportId)}
        onReject={(reportId, notes) => rejectMutation.mutate({ reportId, notes })}
      />
    </div>
  );
}
